import { useState, useEffect, useRef } from 'react';
import { User, Users, Search, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useListAssignees } from '@/hooks/use-list-assignees';
import { cn, getInitials } from '@/lib/utils';

export interface AssigneePickerUserPreview {
  id: string;
  displayName: string;
  avatarUrl?: string | null;
}

export interface AssigneePickerValue {
  assigneeIds: string[];
  assignedTeamId: string | null;
}

interface AssigneePickerProps {
  listId: string;
  currentAssigneeIds?: string[];
  currentTeamId?: string | null;
  currentAssigneeUsers?: AssigneePickerUserPreview[];
  currentTeamName?: string;
  onChange: (value: AssigneePickerValue) => void;
  disabled?: boolean;
  triggerClassName?: string;
}

export function AssigneePicker({
  listId,
  currentAssigneeIds,
  currentTeamId,
  currentAssigneeUsers,
  currentTeamName,
  onChange,
  disabled = false,
  triggerClassName,
}: AssigneePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch assignees with search (also fetch when a team is selected to resolve the team name)
  const { data, isLoading } = useListAssignees(
    open || currentTeamId ? listId : null,
    open ? (debouncedSearch || undefined) : undefined
  );

  // Focus input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setSearch('');
      setDebouncedSearch('');
    }
  }, [open]);

  const users = data?.users ?? [];
  const teams = data?.teams ?? [];
  const selectedAssigneeIds = currentAssigneeIds ?? [];
  const selectedAssigneeIdSet = new Set(selectedAssigneeIds);
  const selectedPreviewUsers = (currentAssigneeUsers ?? []).filter((u) =>
    selectedAssigneeIdSet.has(u.id)
  );

  // Derive team name from fetched teams if not provided as prop
  const resolvedTeamName = currentTeamName ?? teams.find((t) => t.id === currentTeamId)?.name;

  const handleSelectUnassigned = () => {
    onChange({ assigneeIds: [], assignedTeamId: null });
    setOpen(false);
  };

  const handleSelectTeam = (teamId: string) => {
    // Toggle team selection while preserving user assignees
    const nextTeamId = currentTeamId === teamId ? null : teamId;
    onChange({ assigneeIds: selectedAssigneeIds, assignedTeamId: nextTeamId });
  };

  const handleToggleUser = (userId: string) => {
    const nextIds = selectedAssigneeIdSet.has(userId)
      ? selectedAssigneeIds.filter((id) => id !== userId)
      : [...selectedAssigneeIds, userId];
    // Preserve team assignment when toggling users
    onChange({ assigneeIds: nextIds, assignedTeamId: currentTeamId ?? null });
  };

  const currentValue = currentTeamId
    ? `team:${currentTeamId}`
    : selectedAssigneeIds.length > 0
      ? 'users'
      : 'unassigned';

  const assigneeLabel =
    selectedPreviewUsers.length > 0
      ? selectedPreviewUsers.slice(0, 2).map((u) => u.displayName).join(', ')
      : `${selectedAssigneeIds.length} assignee${selectedAssigneeIds.length === 1 ? '' : 's'}`;

  const extraAssignees = selectedAssigneeIds.length - selectedPreviewUsers.slice(0, 2).length;

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn(
            'h-8 gap-2 border-[#444] bg-[#2a2a2a] hover:bg-[#333] focus:ring-0 justify-start',
            triggerClassName
          )}
        >
          <User className="h-4 w-4 text-muted-foreground shrink-0" />
          {selectedAssigneeIds.length > 0 || (currentTeamId && resolvedTeamName) ? (
            <div className="flex items-center gap-2 min-w-0">
              {/* Show user avatars if any */}
              {selectedPreviewUsers.length > 0 && (
                <div className="flex -space-x-1 shrink-0">
                  {selectedPreviewUsers.slice(0, 2).map((u) => (
                    <Avatar key={u.id} className="h-5 w-5 border border-background">
                      <AvatarImage src={u.avatarUrl || undefined} />
                      <AvatarFallback className="text-[8px]">{getInitials(u.displayName)}</AvatarFallback>
                    </Avatar>
                  ))}
                  {extraAssignees > 0 && (
                    <div className="h-5 w-5 rounded-full border border-background bg-muted flex items-center justify-center text-[8px] text-muted-foreground">
                      +{extraAssignees}
                    </div>
                  )}
                </div>
              )}
              {/* Show team icon and name if team is selected */}
              {currentTeamId && resolvedTeamName && (
                <div className="flex items-center gap-1 shrink-0">
                  <Users className="h-4 w-4" />
                  <span className="text-sm truncate max-w-[80px]">{resolvedTeamName}</span>
                </div>
              )}
              {/* Show user label if users selected but no preview available */}
              {selectedAssigneeIds.length > 0 && selectedPreviewUsers.length === 0 && !currentTeamId && (
                <span className="text-sm truncate">{assigneeLabel}</span>
              )}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">Assignees</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[300px] p-0 border-border bg-popover text-popover-foreground"
        align="start"
        sideOffset={4}
      >
        {/* Search input */}
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              placeholder="Search users or teams..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 pr-8 bg-background border-border focus-visible:ring-0 focus-visible:border-primary"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Results — native scroll with explicit height */}
        <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
          <div className="p-1">
            {/* Unassigned option */}
            <button
              onClick={handleSelectUnassigned}
              className={cn(
                'flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm text-left hover:bg-accent transition-colors',
                currentValue === 'unassigned' && 'bg-primary/10 text-primary'
              )}
            >
              <div className="h-5 w-5 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center shrink-0">
                {currentValue === 'unassigned' && <Check className="h-3 w-3" />}
              </div>
              <span>Unassigned</span>
            </button>

            {/* Users section */}
            {users.length > 0 && (
              <div className="mt-2">
                <div className="px-2 py-1 text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Users
                </div>
                {users.map((user) => {
                  const isSelected = selectedAssigneeIdSet.has(user.id);
                  return (
                    <button
                      key={user.id}
                      onClick={() => handleToggleUser(user.id)}
                      className={cn(
                        'flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm text-left hover:bg-accent transition-colors',
                        isSelected && 'bg-primary/10 text-primary'
                      )}
                    >
                      <Avatar className="h-5 w-5 shrink-0">
                        <AvatarImage src={user.avatarUrl || undefined} />
                        <AvatarFallback className="text-[8px]">
                          {getInitials(user.displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{user.displayName}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </div>
                      </div>
                      {isSelected && <Check className="h-4 w-4 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Teams section */}
            {teams.length > 0 && (
              <div className="mt-2">
                <div className="px-2 py-1 text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Teams
                </div>
                {teams.map((team) => {
                  const isSelected = currentValue === `team:${team.id}`;
                  return (
                    <button
                      key={team.id}
                      onClick={() => handleSelectTeam(team.id)}
                      className={cn(
                        'flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm text-left hover:bg-accent transition-colors',
                        isSelected && 'bg-primary/10 text-primary'
                      )}
                    >
                      <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <Users className="h-3 w-3" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{team.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {team.memberCount} member{team.memberCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                      {isSelected && <Check className="h-4 w-4 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Loading state */}
            {isLoading && (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            )}

            {/* Empty state */}
            {!isLoading && users.length === 0 && teams.length === 0 && search && (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                No results for "{search}"
              </div>
            )}

            {!isLoading && users.length === 0 && teams.length === 0 && !search && (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                No assignees available
              </div>
            )}
          </div>
        </div>

        {/* Footer - fixed at bottom */}
        <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border shrink-0">
          You can assign both users and a team.
        </div>
      </PopoverContent>
    </Popover>
  );
}
